import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Download, FileText, ShieldCheck, AlertTriangle, Clock } from "lucide-react";
import { COMPANY } from "@/lib/data";
import { trackConversion } from "@/lib/weeklySpecialTracking";
import { to12h } from "@/lib/datetime";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SignAgreement() {
  const { token } = useParams();
  const [sp] = useSearchParams();
  const returnUrl = (sp.get("return") || "").startsWith("/complete/") ? sp.get("return") : "";
  const [agreement, setAgreement] = useState(null);
  const [terms, setTerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acks, setAcks] = useState([]);
  const [phrase, setPhrase] = useState("");
  const [name, setName] = useState("");
  const [signature, setSignature] = useState("");
  const [initials, setInitials] = useState({ coverage: "", notice: "", damage: "", decline: "", graves: "", screening: "", breakdown: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    axios.get(`${API}/agreement-terms`).then(({ data }) => setTerms(data.sections || [])).catch(() => {});
    axios.get(`${API}/agreements/by-token/${token}`)
      .then(({ data }) => {
        setAgreement(data);
        if (data.terms_sections?.length) setTerms(data.terms_sections);
        setAcks(new Array(data.acknowledgment_list.length).fill(false));
        if (data.signed) setDone(true);
      })
      .catch(() => toast.error("Agreement not found or link expired."))
      .finally(() => setLoading(false));
  }, [token]);

  const allAcks = acks.length > 0 && acks.every(Boolean);
  const phraseOk = agreement && phrase.trim().toUpperCase() === agreement.confirmation_phrase;
  const isDecline = agreement?.coverage_selection === "decline";
  const initialsOk = initials.coverage.trim() && initials.notice.trim() && initials.damage.trim() && initials.graves.trim() && initials.screening.trim() && initials.breakdown.trim() && (!isDecline || initials.decline.trim());
  const canSubmit = allAcks && phraseOk && initialsOk && name.trim().length > 1 && signature.trim().length > 1;

  const submit = async () => {
    if (!canSubmit) { toast.error("Please complete all acknowledgments, initials, the exact statement, name and signature."); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/agreements/${token}/sign`, {
        signed_name: name, signature, confirmation_statement: phrase, acknowledgments: acks, initials,
      });
      setDone(true);
      trackConversion("CompleteRegistration");
      toast.success("Agreement signed! A copy has been emailed to you.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const downloadUrl = `${API}/agreements/${agreement?.agreement_id}/pdf?token=${token}`;

  if (loading) return <div className="min-h-screen bg-[#050505] flex items-center justify-center"><Loader2 className="animate-spin text-[#F2B705]" /></div>;
  if (!agreement) return (
    <div className="min-h-screen bg-[#050505] text-white flex flex-col items-center justify-center gap-4 px-5 text-center">
      <FileText className="text-white/30" size={48} />
      <p className="text-white/60">This agreement link is invalid or has expired.</p>
      <Link to="/" className="text-[#F2B705]">Back to site</Link>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <header className="border-b border-white/10 px-5 md:px-10 h-20 flex items-center gap-3">
        <img src={COMPANY.logo} alt="AutoHire" className="h-11 w-11 object-contain rounded-md" />
        <div>
          <div className="font-display font-extrabold">AutoHire <span className="text-gold-gradient">STL</span></div>
          <div className="text-xs text-white/40">Motor Vehicle Rental Agreement</div>
        </div>
      </header>

      <div className="mx-auto max-w-3xl px-5 md:px-8 py-10">
        {done ? (
          <div className="text-center py-16 flex flex-col items-center gap-4" data-testid="sign-success">
            <CheckCircle2 className="text-[#F2B705]" size={64} />
            <h1 className="font-display font-black text-3xl">Agreement Signed</h1>
            <p className="text-white/60 max-w-md">Thank you, {name || agreement.renter_name}. Your signed rental agreement <b className="text-white">{agreement.agreement_number}</b> has been emailed to you and saved to your rental record.</p>
            {returnUrl && (
              <Link to={returnUrl} data-testid="sign-continue-booking" className="mt-2 inline-flex items-center gap-2 bg-[#F2B705] text-black font-bold px-6 py-3 rounded-md hover:bg-white transition-colors">
                <CheckCircle2 size={18} /> Continue My Booking
              </Link>
            )}
            <a href={downloadUrl} target="_blank" rel="noreferrer" data-testid="sign-download" className={`inline-flex items-center gap-2 font-bold px-6 py-3 rounded-md transition-colors ${returnUrl ? "border border-[#F2B705]/50 text-[#F2B705] hover:bg-[#F2B705] hover:text-black" : "mt-2 bg-[#F2B705] text-black hover:bg-white"}`}>
              <Download size={18} /> Download Agreement PDF
            </a>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 text-[#F2B705] text-sm font-bold uppercase tracking-widest"><ShieldCheck size={16} /> Electronic Signature Required</div>
            <h1 className="font-display font-black text-3xl md:text-4xl mt-2">Review & Sign Your Agreement</h1>
            <p className="text-white/50 mt-2">Agreement <b className="text-white">{agreement.agreement_number}</b> · Reservation {agreement.reservation_number}</p>

            {/* Summary */}
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/10 border border-white/10 rounded-lg overflow-hidden">
              {[
                ["Renter", agreement.renter_name],
                ["Vehicle", `${agreement.vehicle_year || ""} ${agreement.vehicle_make || ""} ${agreement.vehicle_model || ""}`.trim()],
                ["Pickup", `${agreement.rental_start_date} ${to12h(agreement.rental_start_time)}`],
                ["Return", `${agreement.rental_return_date} ${to12h(agreement.rental_return_time)}`],
                ["Coverage", agreement.insurance_option],
                ["Total Charges", agreement.total_charges],
                ["Balance Due", agreement.balance_due],
                ["Duration", agreement.rental_duration],
              ].map(([k, v]) => (
                <div key={k} className="bg-[#0C0C0C] px-4 py-3">
                  <div className="text-[10px] uppercase tracking-widest text-white/40">{k}</div>
                  <div className="font-medium text-sm mt-0.5">{v || "—"}</div>
                </div>
              ))}
            </div>

            {/* Policies */}
            <div className="mt-8 max-h-72 overflow-y-auto rounded-lg border border-white/10 bg-[#0A0A0A] p-5 space-y-4" data-testid="agreement-terms">
              {terms.map((sec, i) => (
                <div key={i}>
                  <h3 className="font-display font-bold text-[#F2B705] text-sm">{sec.title}</h3>
                  {(Array.isArray(sec.paragraphs) ? sec.paragraphs : [sec.paragraphs]).map((para, j) => (
                    <p key={j} className="text-white/55 text-sm mt-1 font-light leading-relaxed">{para}</p>
                  ))}
                </div>
              ))}
              <p className="text-white/40 text-xs">The complete agreement is included in the PDF you will receive after signing.</p>
            </div>

            {/* MISSOURI CDW / CAR RENTAL INSURANCE NOTICE — conspicuous */}
            <div data-testid="mo-cdw-notice" className="mt-8 rounded-xl border-2 border-[#F2B705] bg-[#F2B705]/[0.08] p-5">
              <p className="text-[#F2B705] font-display font-black text-base tracking-wide">COLLISION DAMAGE WAIVER AND CAR RENTAL INSURANCE NOTICE</p>
              <p className="text-white/85 text-xs mt-3 leading-relaxed font-bold uppercase">
                OUR CONTRACT OFFERS FOR AN ADDITIONAL CHARGE COLLISION DAMAGE WAIVER AND CAR RENTAL INSURANCE PRODUCTS.
                BEFORE DECIDING WHETHER TO PURCHASE ANY OF THESE OPTIONAL PRODUCTS, YOU MAY WISH TO DETERMINE WHETHER YOUR
                PERSONAL INSURANCE OR CREDIT CARD PROVIDES YOU COVERAGE DURING THE RENTAL PERIOD. THE PURCHASE OF ANY OF
                THESE OPTIONAL PRODUCTS IS NOT REQUIRED TO RENT A VEHICLE.
              </p>
              <p className="text-[#F2B705]/80 text-[11px] mt-3 font-semibold">You must acknowledge receipt of this notice with your initials in the "Required Initials" section below.</p>
            </div>

            {/* LATE RETURN POLICY — high-visibility callout */}
            <div data-testid="late-policy-callout" className="mt-8 rounded-xl border-2 border-red-500/60 bg-red-500/[0.08] p-5">
              <div className="flex items-center gap-2 text-red-400 font-display font-black text-base uppercase tracking-wide">
                <AlertTriangle size={20} /> Late Return Policy — Please Read
              </div>
              <p className="text-white/80 text-sm mt-3 leading-relaxed">
                Returning the vehicle late costs you money and can lead to recovery of the vehicle. By signing, you agree to the following:
              </p>
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="flex items-start gap-3 bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3">
                  <Clock size={18} className="text-[#F2B705] mt-0.5 shrink-0" />
                  <div>
                    <div className="text-white font-bold text-sm">30-minute grace period</div>
                    <div className="text-white/55 text-xs mt-0.5">After your scheduled return time.</div>
                  </div>
                </div>
                <div className="flex items-start gap-3 bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3">
                  <span className="text-red-400 font-black text-xl leading-none mt-0.5 shrink-0">$50</span>
                  <div>
                    <div className="text-white font-bold text-sm">per hour, after the grace period</div>
                    <div className="text-white/55 text-xs mt-0.5">Charged for every hour (or part of an hour) you're late.</div>
                  </div>
                </div>
                <div className="flex items-start gap-3 bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3">
                  <AlertTriangle size={18} className="text-amber-400 mt-0.5 shrink-0" />
                  <div>
                    <div className="text-white font-bold text-sm">Recovery after 6 hours late</div>
                    <div className="text-white/55 text-xs mt-0.5">AutoHire may begin recovering the vehicle, as permitted by law.</div>
                  </div>
                </div>
                <div className="flex items-start gap-3 bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3">
                  <AlertTriangle size={18} className="text-red-400 mt-0.5 shrink-0" />
                  <div>
                    <div className="text-white font-bold text-sm">Repossession after 24 hours late</div>
                    <div className="text-white/55 text-xs mt-0.5">May be reported to law enforcement where permitted.</div>
                  </div>
                </div>
              </div>
              <p className="text-white/70 text-xs mt-4 leading-relaxed">
                You remain responsible for <b className="text-white">all late fees, recovery/repossession costs, towing, storage, and lost rental income</b> while the vehicle is unavailable. These obligations do not depend on any security deposit.
              </p>
            </div>

            {/* MECHANICAL BREAKDOWN POLICY — high-visibility callout */}
            <div data-testid="breakdown-policy-callout" className="mt-6 rounded-xl border-2 border-amber-400/60 bg-amber-400/[0.06] p-5">
              <div className="flex items-center gap-2 text-amber-300 font-display font-black text-base uppercase tracking-wide">
                <AlertTriangle size={20} /> Mechanical Breakdown & Prorated Refund Policy
              </div>
              <div className="text-white/75 text-xs mt-3 leading-relaxed space-y-2">
                <p>If the vehicle becomes unsafe, disabled, or inoperable from a mechanical failure <b className="text-white">not caused by you</b>: stop driving when necessary, notify AutoHire immediately, and follow all towing, inspection, recovery and repair instructions.</p>
                <p><b className="text-white">A replacement vehicle is not guaranteed</b> — replacements are subject to availability. If none is available, AutoHire may issue a <b className="text-white">prorated refund or rental credit for eligible unused rental time</b> (rental-rate portion only, from the date the breakdown is reported and the vehicle is made available for recovery/inspection).</p>
                <p>Rental time already used remains chargeable. Insurance-related charges, coverage products, damage waivers, delivery fees, service fees, and add-ons are not automatically refundable and are reviewed separately. No refund or credit applies when the breakdown results from an accident, misuse, negligence, prohibited use, unauthorized repairs, failure to report promptly, or continued driving after a warning light, overheating, smoke, unusual noise, or leaking fluid. AutoHire is not responsible for lost wages, rideshare income, missed appointments, lodging, alternate transportation, or other indirect expenses unless approved in writing.</p>
              </div>
              <p className="text-amber-300/80 text-[11px] mt-3 font-semibold">You must enter your initials beside this policy in the "Required Initials" section below.</p>
            </div>

            {/* Coverage & Required Initials */}
            <div data-testid="coverage-initials" className="mt-8 rounded-xl border border-[#F2B705]/30 bg-[#F2B705]/[0.04] p-5">
              <div className="text-[#F2B705] font-display font-bold text-sm uppercase tracking-wide">Rental Coverage & Required Initials</div>
              <div className="mt-3 space-y-2 text-sm">
                <div className="flex justify-between gap-3 border-b border-white/10 pb-2">
                  <span className="text-white/60">Coverage Selection</span>
                  <span className="text-white font-semibold text-right">{agreement.coverage_option || agreement.insurance_option || "—"}</span>
                </div>
                <div className="flex justify-between gap-3">
                  <span className="text-white/60">Damage Waiver / Damage Protection</span>
                  <span className="text-white font-semibold text-right">{agreement.damage_waiver_line || agreement.damage_protection || "—"}</span>
                </div>
              </div>
              <p className="text-white/45 text-xs mt-3 leading-relaxed">Enter your initials to confirm you have read and accept each item below.</p>
              <div className="mt-3 space-y-2.5">
                {[
                  { key: "coverage", label: "Rental Insurance / Coverage Selection" },
                  { key: "notice", label: "I acknowledge receipt of the COLLISION DAMAGE WAIVER AND CAR RENTAL INSURANCE notice above" },
                  { key: "damage", label: "Damage Waiver selection (OPTIONAL — not insurance; covered AutoHire vehicle damage only)" },
                  ...(isDecline ? [{ key: "decline", label: "I voluntarily decline optional rental insurance. I understand I may be personally responsible for liabilities, losses, damages, claims, and expenses not paid by applicable insurance, subject to applicable law" }] : []),
                  { key: "graves", label: "Graves Amendment / ownership-liability provision (49 U.S.C. § 30106)" },
                  { key: "screening", label: "Background Screening Acknowledgment — AutoHire may conduct identity verification and a background screening as part of the reservation and rental approval process; submitting a reservation request does not guarantee approval" },
                  { key: "breakdown", label: "Mechanical Breakdown & Prorated Refund Policy — replacement vehicles are subject to availability; if a qualifying breakdown occurs and no replacement is available, AutoHire may provide a prorated refund or rental credit for eligible unused rental time (rental-rate portion only, per the policy above)" },
                ].map((it) => (
                  <div key={it.key} className="flex items-center gap-3">
                    <input
                      data-testid={`initials-${it.key}`}
                      value={initials[it.key]}
                      onChange={(e) => setInitials((s) => ({ ...s, [it.key]: e.target.value.toUpperCase().slice(0, 5) }))}
                      placeholder="Initials"
                      className="w-24 bg-[#0A0A0A] border border-white/15 rounded-md px-3 py-2 text-sm text-center tracking-widest text-[#F2B705] font-bold focus:outline-none focus:border-[#F2B705]/60"
                    />
                    <span className="text-white/70 text-xs leading-snug">{it.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Acknowledgments */}
            <h2 className="font-display font-bold text-xl mt-8">Acknowledgments</h2>
            <div data-testid="agreement-calendar-notice" className="mt-3 rounded-lg border-2 border-[#F2B705] bg-[#F2B705]/[0.08] p-4">
              <p className="text-[#F2B705] font-black text-sm tracking-wide">HOW WE COUNT RENTAL DAYS</p>
              <p className="text-white/80 text-xs mt-1.5 leading-relaxed">Rental days are counted by <b className="text-white">calendar date from pickup to return</b>, at your agreed pickup/return times. Returning after the scheduled return time may result in additional late-return charges. Example: Monday through Wednesday = 2 rental days. You must check the matching acknowledgment below.</p>
            </div>
            <div className="mt-3 space-y-2">
              {agreement.acknowledgment_list.map((ack, i) => (
                <label key={i} className="flex items-start gap-3 cursor-pointer rounded-md border border-white/10 px-4 py-3 hover:border-white/20">
                  <input type="checkbox" data-testid={`sign-ack-${i}`} checked={acks[i] || false}
                    onChange={(e) => setAcks((arr) => arr.map((v, idx) => idx === i ? e.target.checked : v))}
                    className="mt-0.5 h-4 w-4 accent-[#F2B705]" />
                  <span className="text-sm text-white/75">{ack}</span>
                </label>
              ))}
            </div>

            {/* Exact phrase */}
            <div className="mt-8">
              <label className="text-sm text-white/70">Type exactly: <b className="text-[#F2B705]">{agreement.confirmation_phrase}</b></label>
              <input data-testid="sign-phrase" value={phrase} onChange={(e) => setPhrase(e.target.value)}
                placeholder="Type the statement above"
                className={`mt-2 w-full bg-transparent border-b-2 outline-none py-2.5 rounded-none transition-colors ${phrase && (phraseOk ? "border-green-500" : "border-red-500/60")} ${!phrase && "border-white/15"}`} />
              {phrase && !phraseOk && <p className="text-red-400 text-xs mt-1">Must match exactly.</p>}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-6">
              <div>
                <label className="text-xs uppercase tracking-widest text-white/45">Typed Full Legal Name</label>
                <input data-testid="sign-name" value={name} onChange={(e) => setName(e.target.value)} className="w-full bg-transparent border-b-2 border-white/15 focus:border-[#F2B705] outline-none py-2.5 rounded-none" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-white/45">Electronic Signature</label>
                <input data-testid="sign-signature" value={signature} onChange={(e) => setSignature(e.target.value)} placeholder="Sign your name" className="w-full bg-transparent border-b-2 border-white/15 focus:border-[#F2B705] outline-none py-2.5 rounded-none font-script italic text-xl" />
              </div>
            </div>

            <p className="text-white/35 text-xs mt-5">By submitting, you consent to do business electronically. Your IP address, device, and timestamp are recorded as part of the signature audit trail. This electronic agreement is legally binding upon execution.</p>

            <button data-testid="sign-submit" onClick={submit} disabled={!canSubmit || submitting}
              className="mt-6 w-full bg-[#F2B705] text-black font-bold py-4 rounded-md hover:bg-white transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
              {submitting && <Loader2 className="animate-spin" size={18} />} {submitting ? "Submitting..." : "Sign & Execute Agreement"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
